<template>
  <div class="avatars-list">
    <HeadSection
      title="Avatars"
      :permissionOfSection="permissionOfSection"
    >
      <template #actions>
        <a-col :span="14" class="text-right">
          <a-button
            type="primary"
            size="large"
            :disabled="!createPermission"
            @click="addAvatarCategory"
          >
            <a-icon type="plus" /> Add category
          </a-button>
        </a-col>
      </template>
    </HeadSection>
    <MainContainer :loading="userLoading" :permissionOfSection="permissionOfSection">
      <div class="d-flex justify-content-end mb-3">
        <SearchField
          :value="filters.name"
          :loading="loadingAvatars"
          :disabled="loadingAvatars || !hasListData"
          @search="changeSearchValue"
        />
      </div>
      <a-table
        class="avatars-table"
        :dataSource="avatarsListData"
        :pagination="avatarsListMeta"
        :row-key="(record) => record.id"
        :customRow="customRow"
        :columns="avatarsColumns"
        :loading="loadingAvatars"
        @change="changePagination"
      >
        <template slot="name" slot-scope="name, record">
          <div class="avatars-link">
            <router-link class="text-primary" :to="`/referral/avatars/${record.id}/view`">{{ name }}</router-link>
          </div>
        </template>
      </a-table>
      <TotalCount :count="avatarsListMeta.total" />
    </MainContainer>
  </div>
</template>

<script>
import apiClient from '@/services/axios'
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'
import SearchField from '@/components/custom/form/SearchField.vue'
import TotalCount from '@/views/apps/referral-program/components/TotalCount.vue'
import { setQueryParams, setFilters } from '@/composables/useSetQueryParams.js'
import { getTableMeta } from '@/composables/useGetTableMeta.js'
import { getFilledFilters } from '@/composables/useGetFilledFilters.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    createPermission: {
      type: Boolean,
      default: false,
    },
    editPermission: {
      type: Boolean,
      default: false,
    },
    deletePermission: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    HeadSection,
    MainContainer,
    SearchField,
    TotalCount,
  },
  data: () => ({
    permissionOfSection: 'referral_program',
    avatarsListData: [],
    avatarsListMeta: {
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      pageSizeOptions: ['10', '20', '40'],
    },
    filters: {
      page: undefined,
      per_page: undefined,
      name: undefined,
    },
    loadingAvatars: false,
    avatarsColumns: [
      {
        title: '',
        dataIndex: 'id',
        width: '10%',
        scopedSlots: { customRender: 'id' },
      },
      {
        title: 'Name',
        dataIndex: 'name',
        width: '20%',
        ellipsis: true,
        scopedSlots: { customRender: 'name' },
      },
      {
        title: 'Items',
        dataIndex: 'items',
        width: '20%',
      },
    ],
  }),
  computed: {
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    isHasListItems() {
      return !!this.avatarsListData?.length
    },
    isFiltered() {
      return Object.values(this.filters).some(v => v !== undefined && v !== null && v !== '')
    },
    hasListData() {
      return this.isHasListItems || this.isFiltered
    },
  },
  methods: {
    customRow(referral) {
      return {
        on: {
          click: () => {
            this.$router.push(`/referral/avatars/${referral.id}/view`)
          },
        },
      }
    },
    addAvatarCategory() {
      this.$router.push('/referral/avatars/create')
    },
    changeSearchValue(value) {
      this.filters.name = value
      this.getAvatars()
    },
    changePagination(pagination, _, sorter) {
      this.filters.page = pagination.current
      this.filters.per_page = pagination.pageSize
      this.getAvatars()
    },
    getFilters() {
      setQueryParams(this.filters, this)
      if (!this.isFiltered) {
        return {}
      }

      return getFilledFilters({ filters: this.filters })
    },
    async getAvatars() {
      try {
        this.loadingAvatars = true

        const filters = this.getFilters()

        const url = `${referralApiUrl}/avatar-packages`
        const response = await apiClient.get(url, { params: filters })
        if (response?.data?.data) {
          this.avatarsListData = response.data.data
          const newMeta = getTableMeta(response?.data?.meta)
          Object.assign(this.avatarsListMeta, newMeta)
        }
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loadingAvatars = false
      }
    },
  },
  async created() {
    if (Object.keys(this.$route.query)?.length) {
      setFilters(this.$route.query, this)
    }
    this.getAvatars()
  },
}
</script>

<style lang="scss">
.avatars-list {
  .filters {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    margin-bottom: 10px;
  }
}
.avatars-table {
  .avatars-link {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }
  .ant-table-row {
    cursor: pointer;
  }
  &__edit-mode {
    .ant-table-row {
      cursor: default;
    }
  }
}
[data-kit-theme="default"] .ant-table-empty .ant-table-body {
  overflow: visible !important;
}
.ant-table-column-has-actions {
  overflow: visible !important;
}
</style>
